import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { COMMUNICATION_SERVICE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

const BASE_URL = `${COMMUNICATION_SERVICE_BASE_URL}/api/v1/message-templates`;

export default function useMessageTemplates(page=0, pageSize=50) {
  const authToken = sessionStorage.getItem("authToken");
  const [messageTemplates, setMessageTemplates] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const [loading, setLoading] = useState(false);

  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  // handle axios errors
    const handleAxiosErrors = (err, action = "operation") => {
      let message = "An unexpected error occurred.";
      if (err.response) {
        message =
          err.response.data?.message ||
          `Failed to ${action}: ${err.response.statusText}`;
      } else if (err.request) {
        message =
          "No response from the server. Please check your internet connection.";
      } else {
        message = err.message || "Something went wrong.";
      }
      toast.error(message);
      console.error(`${action} error:`, err);
    };

  // fetch all message templates
  const fetchAllMessageTemplates = useCallback(
    async () => {
      if (!authToken) return;
      if (initialLoad) setLoading(true);
      else setLoading(true);

      try {
        const res = await apiClient.get(
          `${BASE_URL}?page=${page}&size=${pageSize}`
        );

        const templates = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.content)
          ? res.data.content
          : Array.isArray(res.data?.data)
          ? res.data.data
          : [];

        setMessageTemplates(templates.map(formatTemplateData));
        setTotalPages(res.data.totalPages || 1);
        setTotalElements(res.data.totalElements || 0);
      } catch (error) {
        handleAxiosErrors(error, "fetch all templates");
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    },
    [authToken, page, pageSize]
  );

  // fetch template by id
  const fetchTemplateById = useCallback(
    async (templateId) => {
      if (!authToken) return;
      setLoading(true);

      try {
        const res = await apiClient.get(`${BASE_URL}/${templateId}`);
        return res.data;
      } catch (error) {
        handleAxiosErrors(error, "fetch template by id");
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  // create template
  const createTemplate = useCallback(
    async (payload) => {
      if (!authToken) return;
      try {
        const res = await apiClient.post(BASE_URL, payload);

        setMessageTemplates((prev) => [formatTemplateData(res.data), ...prev]);
        toast.success("Templated created successfully");
      } catch (error) {
        handleAxiosErrors(error, "create template");
      }
    },
    [authToken]
  );


  // update template
  const updateTemplate = useCallback(
    async (payload, templateId) => {
      if (!authToken) return;
      try {
        const res = await apiClient.put(`${BASE_URL}/${templateId}`, payload);

        setMessageTemplates((prev) =>
          prev.map((template) =>
            template.id === templateId ? formatTemplateData(res.data) : template
          )
        );
        toast.success("Template updated successfully");
      } catch (error) {
        handleAxiosErrors(error, "update template");
      }
    },
    [authToken]
  );

  // patch template
  const patchTemplate = useCallback(
    async (patches, templateId) => {
      if (!authToken || patches.length === 0) return;

      try {
        const res = await apiClient.patch(`${BASE_URL}/${templateId}`, patches);

        setMessageTemplates((prev) =>
          prev.map((t) => (t.id === templateId ? formatTemplateData(res.data) : t))
        );

        toast.success("Template updated successfully");
        return res.data;
      } catch (error) {
        handleAxiosErrors(error, "update template");
        throw error;
      }
    },
    [authToken]
  );

  // delete template
  const deleteTemplate = useCallback(
    async (templateId) => {
      if (!authToken) return;
      try {
        await apiClient.delete(`${BASE_URL}/${templateId}`);

        setMessageTemplates((prev) => prev.filter((template) => template.id !== templateId));
        toast.success("Template deleted successfully");
      } catch (error) {
        handleAxiosErrors(error, "delete template");
      }
    },
    [authToken]
  );

  const searchTemplates = useCallback(
    async (query = "", searchPage = 0, templateCode = "ALL") => {
      if (!authToken) return;
      setLoading(true);

      try {
        const trimmedQuery = query?.trim() || "";
        const trimmedCode = String(templateCode || "").trim();

        const params = new URLSearchParams({
          page: String(searchPage),
          size: String(pageSize),
        });

        if (trimmedQuery.length >= 3) {
          params.append("q", trimmedQuery);
        }
        if (trimmedCode && trimmedCode !== "ALL") {
          params.append("code", trimmedCode);
        }

        const finalUrl = `${BASE_URL}/search?${params.toString()}`;

        const res = await apiClient.get(finalUrl);

        const templates = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.content)
          ? res.data.content
          : Array.isArray(res.data?.data)
          ? res.data.data
          : [];

        setMessageTemplates(templates.map(formatTemplateData));
        setTotalPages(res.data?.totalPages || 1);
        setTotalElements(res.data?.totalElements || templates.length || 0);
      } catch (error) {
        handleAxiosErrors(error, "search message templates");
      } finally {
        setLoading(false);
      }
    },
    [authToken, pageSize]
  );

  // fetch automatically on mount
  useEffect(() => {
    if (authToken) fetchAllMessageTemplates();
  }, [authToken, fetchAllMessageTemplates]);

  return {
    messageTemplates,
    loading,
    initialLoad,
    fetchTemplateById,
    createTemplate,
    updateTemplate,
    patchTemplate,
    deleteTemplate,
    searchTemplates,
    totalPages,
    totalElements,
  };
}

const formatTemplateData = (template = {}) => {
  return {
    id: template.id || null,
    name: template.name || "",
    subject: template.subject || "",
    body: template.body || "",
    category: template.category || "GENERAL",
    createdAt: template.createdAt || null,
    updatedAt: template.updatedAt || null,
    ...template,
  };
};
